import React, { type ReactNode } from 'react';
import { Link } from '../Link/Link';

/**
 * Handler functions for parsing backend messages with special tags.
 * All handlers are optional - if not provided, sensible defaults will be used.
 */
export interface BackendMessageHandlers {
  /** Handler for {break} tags. Default: <br /> */
  break?: ReactNode;
  /** Handler for <maillink> tags. Default: renders as plain text */
  maillink?: (content: string) => ReactNode;
  /** Handler for <migrate> tags. Default: renders as plain text */
  migrate?: (content: string) => ReactNode;
  /** Handler for <strong> tags. Default: <strong> element */
  strong?: (content: string) => ReactNode;
  /** Handler for <u> tags. Default: <u> element */
  u?: (content: string) => ReactNode;
}

/**
 * Parses backend error messages containing ICU-style tags and converts them to React elements.
 * This custom parser is needed because the production build uses the no-parser alias for
 * @formatjs/icu-messageformat-parser, which affects all imports from that package.
 *
 * Supports:
 * - {break} - line break (default: <br />)
 * - <maillink>email</maillink> - email link (default: plain text)
 * - <migrate>text</migrate> - migration link (default: plain text)
 * - <strong>text</strong> - bold text (default: <strong> element)
 * - <u>text</u> - underlined text (default: <u> element)
 *
 * @param message - The backend error message string containing tags
 * @param handlers - Optional handlers for each tag type. Unspecified handlers use defaults.
 * @returns React element with parsed and rendered content
 *
 * @example
 * ```tsx
 * // Minimal - only override what you need
 * const result = parseBackendMessage(
 *   'Contact <maillink>support@example.com</maillink>',
 *   {
 *     maillink: (email) => <a href={`mailto:${email}`}>{email}</a>,
 *   }
 * );
 *
 * // No handlers needed for simple formatting
 * const result2 = parseBackendMessage(
 *   'This is <strong>important</strong>{break}Please note.',
 * );
 * ```
 */
export const parseBackendMessage = (
  message: string,
  handlers: BackendMessageHandlers = {},
): ReactNode => {
  // Provide default handlers for all tag types
  const defaultHandlers = {
    break: <br />,
    maillink: (content: string) => <Link href={`mailto:${content}`}>{content}</Link>,
    migrate: (content: string) => content,
    strong: (content: string) => <strong>{content}</strong>,
    u: (content: string) => <u>{content}</u>,
  };

  // Merge provided handlers with defaults
  const resolvedHandlers = {
    break: handlers.break ?? defaultHandlers.break,
    maillink: handlers.maillink ?? defaultHandlers.maillink,
    migrate: handlers.migrate ?? defaultHandlers.migrate,
    strong: handlers.strong ?? defaultHandlers.strong,
    u: handlers.u ?? defaultHandlers.u,
  };
  const parts: ReactNode[] = [];
  let remaining = message;
  let key = 0;

  while (remaining.length > 0) {
    let matched = false;

    // Check for {break}
    if (remaining.startsWith('{break}')) {
      parts.push(<React.Fragment key={`break-${key}`}>{resolvedHandlers.break}</React.Fragment>);
      key += 1;
      remaining = remaining.slice(7);
      matched = true;
    }

    // Check for <maillink>
    if (!matched) {
      const maillinkMatch = remaining.match(/^<maillink>([^<]*)<\/maillink>/);
      if (maillinkMatch) {
        parts.push(
          <React.Fragment key={`maillink-${key}`}>
            {resolvedHandlers.maillink(maillinkMatch[1])}
          </React.Fragment>,
        );
        key += 1;
        remaining = remaining.slice(maillinkMatch[0].length);
        matched = true;
      }
    }

    // Check for <migrate>
    if (!matched) {
      const migrateMatch = remaining.match(/^<migrate>([^<]*)<\/migrate>/);
      if (migrateMatch) {
        parts.push(
          <React.Fragment key={`migrate-${key}`}>
            {resolvedHandlers.migrate(migrateMatch[1])}
          </React.Fragment>,
        );
        key += 1;
        remaining = remaining.slice(migrateMatch[0].length);
        matched = true;
      }
    }

    // Check for <strong>
    if (!matched) {
      const strongMatch = remaining.match(/^<strong>([^<]*)<\/strong>/);
      if (strongMatch) {
        parts.push(
          <React.Fragment key={`strong-${key}`}>
            {resolvedHandlers.strong(strongMatch[1])}
          </React.Fragment>,
        );
        key += 1;
        remaining = remaining.slice(strongMatch[0].length);
        matched = true;
      }
    }

    // Check for <u>
    if (!matched) {
      const uMatch = remaining.match(/^<u>([^<]*)<\/u>/);
      if (uMatch) {
        parts.push(
          <React.Fragment key={`u-${key}`}>{resolvedHandlers.u(uMatch[1])}</React.Fragment>,
        );
        key += 1;
        remaining = remaining.slice(uMatch[0].length);
        matched = true;
      }
    }

    // If no special tag matched, process plain text
    if (!matched) {
      // Find the next special character
      const breakPos = remaining.indexOf('{break}');
      const maillinkPos = remaining.indexOf('<maillink>');
      const migratePos = remaining.indexOf('<migrate>');
      const strongPos = remaining.indexOf('<strong>');
      const uPos = remaining.indexOf('<u>');

      const positions = [breakPos, maillinkPos, migratePos, strongPos, uPos].filter(
        (pos) => pos !== -1,
      );
      const nextSpecial = positions.length > 0 ? Math.min(...positions) : -1;

      if (nextSpecial === -1) {
        // No more special tags, add the rest as plain text
        parts.push(remaining);
        break;
      }

      if (nextSpecial === 0) {
        // We're at a tag-like position but it didn't match (malformed tag)
        // Consume one character at a time to avoid infinite loop
        // This handles cases like "<maillink>incomplete" where the tag is never closed
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        // Add plain text before the next special tag
        parts.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      }
    }
  }

  return <>{parts}</>;
};
